/**
 * Hook for subscribing to contact WebSocket events using DCRF streams.
 */
import { useEffect, useCallback, useState } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';
import type { CampaignContactDetail } from '../types';

export type ContactObserverEvent = Partial<CampaignContactDetail> & {
  id: string;
  status: string;
};

interface UseContactWebSocketOptions {
  onCallStarted?: (event: ContactObserverEvent) => void;
  onCallEnded?: (event: ContactObserverEvent) => void;
  onStatusChange?: (event: ContactObserverEvent) => void;
}

interface UseContactWebSocketReturn {
  isConnected: boolean;
  isSubscribed: boolean;
  lastEvent: ContactObserverEvent | null;
  activeCall: { callId: string; phone: string; agentName?: string } | null;
}

/**
 * Subscribe to contact updates and call status changes.
 */
export function useContactWebSocket(
  contactId: string | undefined,
  options: UseContactWebSocketOptions = {},
): UseContactWebSocketReturn {
  const { onCallStarted, onCallEnded, onStatusChange } = options;
  const { state, addObserver, removeObserver } = useWebSocket();

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [lastEvent, setLastEvent] = useState<ContactObserverEvent | null>(null);
  const [activeCall, setActiveCall] = useState<{ callId: string; phone: string; agentName?: string } | null>(null);

  const handleEvent = useCallback(
    (data: ContactObserverEvent, action: string) => {
      if (action !== 'update' && action !== 'create') {
        return;
      }

      const normalized = transformKeys<ContactObserverEvent>(data);
      setLastEvent(normalized);
      onStatusChange?.(normalized);

      const isInProgress = normalized.status === 'in_progress';
      if (isInProgress) {
        setActiveCall({ callId: normalized.id, phone: '', agentName: undefined });
        onCallStarted?.(normalized);
      } else {
        setActiveCall(null);
        onCallEnded?.(normalized);
      }
    },
    [onCallStarted, onCallEnded, onStatusChange],
  );

  useEffect(() => {
    if (!contactId || !state.isConnected) {
      setIsSubscribed(false);
      return;
    }

    const observerId = addObserver(
      'contact',
      { data: { target: contactId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
      },
    );
    setIsSubscribed(true);

    return () => {
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [contactId, state.isConnected, addObserver, removeObserver, handleEvent]);

  return {
    isConnected: state.isConnected,
    isSubscribed,
    lastEvent,
    activeCall,
  };
}

export default useContactWebSocket;
